'use client';

import { FC, useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { updateCartList } from '@screens/Cart';
import {
  addPersonalAccountInfoThunk,
  PersonalAccountAddressInfoType,
  PersonalAccountInfoType
} from '@screens/PersonalAccount';
import { Form } from '@screens/PlacingOrder/ui/Form/Form';
import { cdekCalculate } from '@shared/api/cdekCalculate/cdekCalculate';
import { ROUTES } from '@shared/constants/routes';
import { useAppDispatch } from '@shared/hooks/redux';
import { useOrderPaymentPlate } from '@shared/hooks/useOrderPaymentPlate';
import { usePersonalAccountFields } from '@shared/hooks/usePersonalAccountFields';
import { CdekCalculateInfo, CdekOffice } from '@shared/types/cdek';
import { DeliveryCompany } from '@shared/types/enums/delivery.enum';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { PaymentMethodType, YandexPayType } from '@shared/types/enums/pay.enum';
import { WithNeedUpdateTokenProps } from '@shared/types/global';
import { OrderBankInfoType, OrderYandexInfoType } from '@shared/types/order';
import { ProductsType } from '@shared/types/product';
import { Container } from '@shared/ui/Container';
import { PageHead } from '@shared/ui/PageHead';
import { VStack } from '@shared/ui/Stack';
import { validateEmail, validatePostalCode } from '@shared/utils/fieldValidators/fieldValidators';
import { getProductCardData } from '@shared/utils/getProductCardData/getProductCardData';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';
import { DeliveryPlate } from '@widgets/DeliveryPlate/DeliveryPlate';
import { PaymentPlate } from '@widgets/PaymentPlate/PaymentPlate';
import { RefreshTokenState } from '@widgets/RefreshTokenState/RefreshTokenState';

import { createOrderBankThunk, createOrderYandexPaymentThunk } from '../';
import cls from './PlacingOrder.module.scss';

export enum ManagerCallTabs {
  YES = 'yes',
  NO = 'no'
}

type PlacingOrderProps = {
  profileInfo?: PersonalAccountInfoType;
  addressInfo?: PersonalAccountAddressInfoType;
  cartProducts?: ProductsType;
} & WithNeedUpdateTokenProps;

const crumbs = [
  { label: 'Главная', url: ROUTES.MAIN },
  { label: 'Корзина', url: '/korzina' },
  { label: 'Оформление заказа', url: '' }
];

export const PlacingOrder: FC<PlacingOrderProps> = ({
  cartProducts = {},
  profileInfo,
  addressInfo,
  needUpdateToken
}) => {
  const dispatch = useAppDispatch();

  const { push, replace } = useRouter();

  const [paymentTab, setPaymentTab] = useState<PaymentMethodType>(PaymentMethodType.BANK_CARD);

  const [selectedPoint, setSelectedPoint] = useState<CdekOffice>();
  const [calculateInfo, setCalculateInfo] = useState<CdekCalculateInfo>();

  const [deliveryCompany, setDeliveryCompany] = useState<DeliveryCompany>(DeliveryCompany.CDEK);

  const [managerCall, setManagerCall] = useState<ManagerCallTabs>(ManagerCallTabs.YES);

  const [errorDeliveryPoint, setErrorDeliveryPoint] = useState('');

  const hasProducts = cartProducts && !!Object.values(cartProducts).length;

  const isCdekWithoutDeliveryPoint = !selectedPoint && deliveryCompany === DeliveryCompany.CDEK;

  const {
    firstName,
    secondName,
    lastName,
    email,
    phoneNumber,
    postalCode,
    region,
    city,
    street,
    houseNumber,
    corpus,
    building,
    apartment,
    floor,
    comment,
    errorEmail,
    setErrorFirstName,
    setErrorLastName,
    setErrorEmail,
    setErrorPostalCode,
    setErrorRegion,
    setErrorCity,
    setErrorStreet,
    setErrorHouseNumber,
    ...otherProps
  } = usePersonalAccountFields({ profileInfo, addressInfo });

  const productCardData = getProductCardData(cartProducts);

  const orderPayment = useOrderPaymentPlate(productCardData);

  const validate = () => {
    const isInvalidCommon = !firstName || !lastName || !validateEmail(email);

    if (isInvalidCommon) {
      setErrorFirstName(!firstName);
      setErrorLastName(!lastName);
      setErrorEmail(!validateEmail(email));
      return false;
    }

    if (deliveryCompany === DeliveryCompany.CDEK && !selectedPoint) {
      setErrorDeliveryPoint(isCdekWithoutDeliveryPoint ? 'Выберите пункт доставки' : '');
      return false;
    }

    if (deliveryCompany === DeliveryCompany.BAIKAL) {
      const isInvalidBaikal = !validatePostalCode(postalCode) || !region || !city || !street || !houseNumber;

      if (isInvalidBaikal) {
        setErrorPostalCode(!validatePostalCode(postalCode));
        setErrorRegion(!region);
        setErrorCity(!city);
        setErrorStreet(!street);
        setErrorHouseNumber(!houseNumber);
        return false;
      }
    }

    return true;
  };

  const handleClickCreateOrder = async () => {
    if (!validate()) return;

    await dispatch(
      addPersonalAccountInfoThunk({
        profileInfo: {
          firstName,
          secondName,
          lastName,
          account: {
            email
          }
        },
        addressInfo: {
          postalCode,
          region,
          city,
          street,
          house: houseNumber,
          corpus,
          building,
          apartment,
          floor
        }
      })
    );

    let order;

    if (paymentTab === PaymentMethodType.BANK_CARD) {
      const { payload } = await dispatch(
        createOrderBankThunk({
          comment,
          deliveryCompany,
          managerCall: managerCall === ManagerCallTabs.YES,
          deliveryPoint: selectedPoint?.code
        })
      );

      const { host, orderNumber } = payload as OrderBankInfoType;
      if (host && orderNumber) {
        order = orderNumber;
      }
    } else {
      const { payload } = await dispatch(
        createOrderYandexPaymentThunk({
          comment,
          type: paymentTab === PaymentMethodType.YANDEX_PAY ? YandexPayType.CARD : YandexPayType.SPLIT,
          deliveryCompany,
          managerCall: managerCall === ManagerCallTabs.YES,
          deliveryPoint: selectedPoint?.code
        })
      );

      const { orderNumber } = payload as OrderYandexInfoType;

      if (orderNumber) {
        order = orderNumber;
      }
    }

    if (order) {
      const selectedIds = orderPayment.selectedCartProductValues.map(({ id }) => id);

      const updatedCartList = productCardData.reduce(
        (acc: Array<{ productId: string; quantity: string }>, { id, quantity }) => {
          if (!selectedIds.includes(id)) {
            acc.push({ productId: id, quantity: String(quantity) });
          }

          return acc;
        },
        []
      );

      dispatch(updateCartList(updatedCartList));

      push(`${ROUTES.ORDERS_HISTORY}/${order}`);
    }

    sendMetrics(Metrics.PURCHASE_INTENT);
  };

  const handleChangePaymentMethod = (tab: string) => {
    setPaymentTab(tab as PaymentMethodType);
  };

  const onChangeSelectedPoint = useCallback(
    async (point?: CdekOffice) => {
      setSelectedPoint(point);

      if (point?.location?.city_code) {
        const res = await cdekCalculate({
          timestamp: new Date().toISOString(),
          city_code: point.location.city_code,
          productId: Object.values(cartProducts)
            .filter((el) => el.selected)
            .map((el) => ({
              id: el.productId || '',
              count: el.quantity || 1
            }))
        });

        setCalculateInfo(res);
      } else {
        setCalculateInfo(undefined);
      }
    },
    [cartProducts]
  );

  const onChangeDeliveryCompany = useCallback((tab: string) => {
    setDeliveryCompany(tab as DeliveryCompany);
    setErrorDeliveryPoint('');
    setManagerCall(ManagerCallTabs.YES);
  }, []);

  const onClickManagerCallTab = useCallback((tab: string) => {
    setManagerCall(tab as ManagerCallTabs);
  }, []);

  useEffect(() => {
    if (!hasProducts && !needUpdateToken) {
      replace(ROUTES.CART);
    }
  }, [hasProducts, needUpdateToken, replace]);

  return (
    <>
      <PageHead breadcrumbs={crumbs} />
      <Container>
        <div className={cls.wrapper}>
          <VStack>
            <Form
              onClickPaymentMethodTab={handleChangePaymentMethod}
              totalAmount={orderPayment.totalAmount}
              {...{
                lastName,
                firstName,
                secondName,
                email,
                phoneNumber,
                postalCode,
                region,
                city,
                street,
                houseNumber,
                corpus,
                building,
                apartment,
                floor,
                comment,
                errorEmail,
                selectedPoint,
                deliveryCompany,
                errorDeliveryPoint,
                onChangeSelectedPoint,
                onChangeDeliveryCompany,
                onClickManagerCallTab,
                setErrorDeliveryPoint,
                ...otherProps
              }}
            />
          </VStack>
          <VStack className={cls.rightContent}>
            <PaymentPlate
              buttonLabel={'Оформить заказ'}
              onClickButton={handleClickCreateOrder}
              paymentMethod={paymentTab}
              {...orderPayment}
            >
              <DeliveryPlate
                amount={calculateInfo?.delivery_sum}
                orderPrice={orderPayment.totalAmount}
                date={calculateInfo?.delivery_date_range}
                {...{ deliveryCompany }}
                products={Object.values(cartProducts)
                  .filter((el) => el.selected)
                  .map((el) => ({
                    productData: el,
                    quantity: el.quantity || 1
                  }))}
                address={{
                  postalCode,
                  region,
                  city,
                  street,
                  house: houseNumber,
                  corpus,
                  building,
                  apartment,
                  floor
                }}
              />
            </PaymentPlate>
          </VStack>
        </div>
      </Container>
      <RefreshTokenState {...{ needUpdateToken }} />
    </>
  );
};
